import { useState, type JSX } from 'react';
import type {
  OtherClergryPerson,
  PersonnelData,
  PersonnelPerson,
  SeniorMinisterPerson,
} from '../../api-client';
import { RegistrarContact } from '../access/RegistrarContact';

interface PersonnelFormProps {
  data: PersonnelData;
  onChange: (data: PersonnelData) => void;
  parishName: string;
}

export function PersonnelForm({ data, onChange, parishName }: PersonnelFormProps): JSX.Element {
  const churchType = classifyChurchType(data.parish.church_type);

  return (
    <form className="space-y-6 pb-6">
      <ClergySectionection
        vicar={data.senior_minister}
        onVicarChange={(vicar) => onChange({ ...data, senior_minister: vicar })}
        otherClergy={data.other_clergy}
        onOtherClergeyChange={(other_clergy) => onChange({ ...data, other_clergy })}
      />

      <WardensSection
        churchType={churchType}
        wardens={data.wardens}
        onWardensChange={(wardens) => onChange({ ...data, wardens })}
      />

      <ParishCouncilSection
        members={data.parish_council}
        onChange={(parish_council) => onChange({ ...data, parish_council })}
      />

      <SynodRepsSection
        reps={data.synod_reps}
        onChange={(synod_reps) => onChange({ ...data, synod_reps })}
      />

      <SingleRoleSection
        title="VPSO (Vulnerable Persons Safety Officer)"
        person={data.vpso}
        onChange={(vpso) => onChange({ ...data, vpso })}
      />

      <SingleRoleSection
        title="Safe Ministry Lead Officer"
        person={data.safety_lead_officer}
        onChange={(safety_lead_officer) => onChange({ ...data, safety_lead_officer })}
      />

      <SingleRoleSection
        title="Treasurer"
        person={data.treasurer}
        onChange={(treasurer) => onChange({ ...data, treasurer })}
      />

      <AdministratorSection
        parishName={parishName}
        person={data.administrator}
        onChange={(administrator) => onChange({ ...data, administrator })}
      />

      {churchType === 'parish' && (
        <NominatorsSection
          nominators={data.nominators}
          onChange={(nominators) => onChange({ ...data, nominators })}
        />
      )}
    </form>
  );
}

/* ───────────── Sections ───────────── */

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <section className="panel">
      <h3 className="section-heading">{title}</h3>
      {description && <p className="section-sub">{description}</p>}
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function ClergySectionection({
  vicar,
  onVicarChange,
  otherClergy,
  onOtherClergeyChange,
}: {
  vicar: SeniorMinisterPerson | null;
  onVicarChange: (p: SeniorMinisterPerson | null) => void;
  otherClergy: OtherClergryPerson[];
  onOtherClergeyChange: (c: OtherClergryPerson[]) => void;
}): JSX.Element {
  if (!vicar) {
    return (
      <Section title="Clergy:">
        <p className="text-sm text-brand-muted">
          No Vicar assigned. Speak to the Registrar — this is a Bishop's licensing matter.
        </p>
        {/* The route to the Registrar goes with the instruction (UX-6, Miranda r2). */}
        <RegistrarContact className="text-sm text-brand-muted" />
      </Section>
    );
  }

  const fullName = [vicar.first_name, vicar.last_name].filter(Boolean).join(' ');
  const updateVicar = (patch: Partial<SeniorMinisterPerson>): void =>
    onVicarChange({ ...vicar, ...patch });

  return (
    <Section
      title="Clergy:"
      description="Vicar name is read-only — changes are a Bishop's licensing matter. Contact details are editable."
    >
      <div>
        <h4 className="mb-3 font-medium">Vicar</h4>
        <Field label="Name" value={fullName || '—'} readOnly />
        <FieldInput
          label="Email"
          type="email"
          value={vicar.email ?? ''}
          onChange={(v) => updateVicar({ email: v || null })}
        />
        <FieldInput
          label="Phone"
          value={vicar.phone ?? ''}
          onChange={(v) => updateVicar({ phone: v || null })}
        />
        <FieldInput
          label="Mailing street"
          value={vicar.mailing_street ?? ''}
          onChange={(v) => updateVicar({ mailing_street: v || null })}
        />
        <div className="grid grid-cols-2 gap-3">
          <FieldInput
            label="City"
            value={vicar.mailing_city ?? ''}
            onChange={(v) => updateVicar({ mailing_city: v || null })}
          />
          <FieldInput
            label="Region"
            value={vicar.mailing_state ?? ''}
            onChange={(v) => updateVicar({ mailing_state: v || null })}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <FieldInput
            label="Postcode"
            value={vicar.mailing_zip ?? ''}
            onChange={(v) => updateVicar({ mailing_zip: v || null })}
          />
          <FieldInput
            label="Country"
            value={vicar.mailing_country ?? ''}
            onChange={(v) => updateVicar({ mailing_country: v || null })}
          />
        </div>
      </div>
      <div className="mt-6 border-t border-edge pt-6">
        <h4 className="mb-3 font-medium">Other Clergy</h4>
        <p className="text-sm text-brand-muted">
          Populated from Zoho Contacts linked to this parish. To change these roles, contact the
          Registrar.
        </p>
        <RegistrarContact className="mt-1 mb-4 text-sm text-brand-muted" />
        <div className="space-y-3">
          {otherClergy.length === 0 && <p className="text-sm text-brand-muted">None assigned.</p>}
          {otherClergy.map((clergy, i) => {
            const fullName = [clergy.first_name, clergy.last_name].filter(Boolean).join(' ') || '—';
            return (
              <div
                key={i}
                className="rounded-control border border-brand-border bg-brand-bg/30 p-3"
              >
                <p className="mb-3 text-sm font-medium">Position {i + 2}</p>
                <div className="space-y-2 text-sm">
                  <Field label="Name" value={fullName} readOnly />
                  {clergy.email && <Field label="Email" value={clergy.email} readOnly />}
                  {clergy.phone && <Field label="Phone" value={clergy.phone} readOnly />}
                </div>
                <button
                  type="button"
                  className="mt-3 rounded-control border border-brand-border px-3 py-2 text-sm text-brand-muted hover:bg-brand-bg"
                  onClick={() => onOtherClergeyChange(otherClergy.filter((_, j) => j !== i))}
                >
                  Delete
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </Section>
  );
}

function WardensSection({
  churchType,
  wardens,
  onWardensChange,
}: {
  churchType: ChurchType;
  wardens: PersonnelData['wardens'];
  onWardensChange: (w: PersonnelData['wardens']) => void;
}): JSX.Element {
  const isParish = churchType === 'parish';

  const updateOne = (
    slot: keyof PersonnelData['wardens'],
    patch: Partial<PersonnelPerson>,
  ): void => {
    const current = wardens[slot] ?? blankPerson();
    const updated = { ...current, ...patch };
    const isEmpty =
      !updated.id && !updated.first_name && !updated.last_name && !updated.email && !updated.phone;
    onWardensChange({ ...wardens, [slot]: isEmpty ? null : updated });
  };

  return (
    <Section
      title="Wardens"
      description={
        isParish
          ? "Parish layout: Vicar's Warden (appointed) and People's Warden (elected)."
          : "Branch Church layout: Warden — Bishop's (appointed) and Warden — People's (elected)."
      }
    >
      {isParish ? (
        <WardenSlot
          label="Vicar's Warden (appointed by the Vicar)"
          person={wardens.vicars_warden}
          onChange={(patch) => updateOne('vicars_warden', patch)}
          rowKey={`vicars_warden:${wardens.vicars_warden?.id ?? ''}`}
        />
      ) : (
        <WardenSlot
          label="Warden — Bishop's (appointed by the Bishop)"
          person={wardens.bishops_warden}
          onChange={(patch) => updateOne('bishops_warden', patch)}
          rowKey={`bishops_warden:${wardens.bishops_warden?.id ?? ''}`}
        />
      )}
      <WardenSlot
        label="People's Warden (elected by members)"
        person={wardens.peoples_warden}
        onChange={(patch) => updateOne('peoples_warden', patch)}
        rowKey={`peoples_warden:${wardens.peoples_warden?.id ?? ''}`}
      />
    </Section>
  );
}

function WardenSlot({
  label,
  person,
  onChange,
  rowKey,
}: {
  label: string;
  person: PersonnelPerson | null;
  onChange: (patch: Partial<PersonnelPerson>) => void;
  rowKey: string;
}): JSX.Element {
  const current = person ?? blankPerson();
  return (
    <div
      className={`rounded-control border p-3 ${
        person ? 'border-brand-border bg-brand-bg/30' : 'border-dashed border-brand-border'
      }`}
    >
      <p className="text-sm font-medium">{label}</p>
      {!person && (
        <p className="mt-0.5 text-xs text-brand-muted">
          None currently appointed — fill in details to appoint.
        </p>
      )}
      <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-3">
        <NameField
          label="Name"
          first_name={current.first_name}
          last_name={current.last_name}
          onChange={(first, last) => onChange({ first_name: first, last_name: last })}
          rowKey={rowKey}
        />
        <FieldInput
          label="Email"
          type="email"
          value={current.email ?? ''}
          onChange={(v) => onChange({ email: v || null })}
        />
        <FieldInput
          label="Phone"
          value={current.phone ?? ''}
          onChange={(v) => onChange({ phone: v || null })}
        />
      </div>
    </div>
  );
}

function ParishCouncilSection({
  members,
  onChange,
}: {
  members: PersonnelPerson[];
  onChange: (m: PersonnelPerson[]) => void;
}): JSX.Element {
  return (
    <Section title="Parish Council Members" description="Add as many rows as needed.">
      <PeopleList people={members} onChange={onChange} addLabel="Add member" />
    </Section>
  );
}

function SynodRepsSection({
  reps,
  onChange,
}: {
  reps: PersonnelPerson[];
  onChange: (r: PersonnelPerson[]) => void;
}): JSX.Element {
  const max = 3;
  return (
    <Section title="Synod Representatives" description={`Up to ${max} representatives.`}>
      <PeopleList people={reps} onChange={onChange} addLabel="Add representative" maxRows={max} />
    </Section>
  );
}

function SingleRoleSection({
  title,
  person,
  onChange,
}: {
  title: string;
  person: PersonnelPerson | null;
  onChange: (p: PersonnelPerson | null) => void;
}): JSX.Element {
  const current = person ?? blankPerson();
  const update = (patch: Partial<PersonnelPerson>): void => onChange({ ...current, ...patch });
  return (
    <Section title={title}>
      <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
        <NameField
          label="Name"
          first_name={current.first_name}
          last_name={current.last_name}
          onChange={(first, last) => update({ first_name: first, last_name: last })}
          rowKey={`single:${title}:${current.id}`}
        />
        <FieldInput
          label="Email"
          type="email"
          value={current.email ?? ''}
          onChange={(v) => update({ email: v || null })}
        />
        <FieldInput
          label="Phone"
          value={current.phone ?? ''}
          onChange={(v) => update({ phone: v || null })}
        />
      </div>
    </Section>
  );
}

function AdministratorSection({
  parishName,
  person,
  onChange,
}: {
  parishName: string;
  person: PersonnelPerson | null;
  onChange: (p: PersonnelPerson | null) => void;
}): JSX.Element {
  const [hasAdmin, setHasAdmin] = useState(person !== null);
  return (
    <Section title="Church Administrator">
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={hasAdmin}
          onChange={(e) => {
            setHasAdmin(e.target.checked);
            if (!e.target.checked) onChange(null);
            else if (person === null) onChange(blankPerson());
          }}
        />
        <span>{parishName} has a Church Administrator</span>
      </label>
      {hasAdmin && (
        <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
          <NameField
            label="Name"
            first_name={person?.first_name ?? null}
            last_name={person?.last_name ?? null}
            onChange={(first, last) => {
              const next = person ?? blankPerson();
              onChange({ ...next, first_name: first, last_name: last });
            }}
            rowKey={`administrator:${person?.id ?? 'new'}`}
          />
          <FieldInput
            label="Email"
            type="email"
            value={person?.email ?? ''}
            onChange={(v) => onChange({ ...(person ?? blankPerson()), email: v || null })}
          />
          <FieldInput
            label="Phone"
            value={person?.phone ?? ''}
            onChange={(v) => onChange({ ...(person ?? blankPerson()), phone: v || null })}
          />
        </div>
      )}
    </Section>
  );
}

function NominatorsSection({
  nominators,
  onChange,
}: {
  nominators: PersonnelPerson[];
  onChange: (n: PersonnelPerson[]) => void;
}): JSX.Element {
  const max = 4;
  const padded: (PersonnelPerson | null)[] = [...nominators];
  while (padded.length < max) padded.push(null);

  const patch = (i: number, fields: Partial<PersonnelPerson>): void => {
    const next: PersonnelPerson[] = [];
    for (let j = 0; j < max; j += 1) {
      const existing = nominators[j] ?? null;
      if (j === i) {
        const base = existing ?? blankPerson();
        next.push({ ...base, ...fields });
      } else if (existing) {
        next.push(existing);
      }
    }
    onChange(next);
  };

  return (
    <Section
      title="Parish Nominators"
      description="Hidden for Branch / Sub-Branch churches. Up to 4 rows."
    >
      {padded.slice(0, max).map((p, i) => (
        <div key={i} className="rounded-control border border-brand-border bg-brand-bg/30 p-3">
          <p className="text-sm font-medium">Nominator {i + 1}</p>
          <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-3">
            <NameField
              label="Name"
              first_name={p?.first_name ?? null}
              last_name={p?.last_name ?? null}
              onChange={(first, last) => patch(i, { first_name: first, last_name: last })}
              rowKey={`nominator:${i}:${p?.id ?? 'new'}`}
            />
            <FieldInput
              label="Email"
              type="email"
              value={p?.email ?? ''}
              onChange={(v) => patch(i, { email: v || null })}
            />
            <FieldInput
              label="Phone"
              value={p?.phone ?? ''}
              onChange={(v) => patch(i, { phone: v || null })}
            />
          </div>
        </div>
      ))}
    </Section>
  );
}

/* ───────────── Reusable bits ───────────── */

function PeopleList({
  people,
  onChange,
  addLabel,
  maxRows,
}: {
  people: PersonnelPerson[];
  onChange: (p: PersonnelPerson[]) => void;
  addLabel: string;
  maxRows?: number;
}): JSX.Element {
  return (
    <div className="space-y-2">
      {people.length === 0 && <p className="text-sm text-brand-muted">None yet.</p>}
      {people.map((p, i) => (
        <div
          key={p.id || `new-${i}`}
          className="grid grid-cols-1 gap-2 md:grid-cols-[1fr_1fr_1fr_auto]"
        >
          <NameField
            label="Name"
            first_name={p.first_name}
            last_name={p.last_name}
            onChange={(first, last) => {
              const next = [...people];
              const row = next[i];
              if (!row) return;
              next[i] = { ...row, first_name: first, last_name: last };
              onChange(next);
            }}
            rowKey={`peoplelist:${i}:${p.id || 'new'}`}
          />
          <FieldInput
            label="Email"
            type="email"
            value={p.email ?? ''}
            onChange={(v) => {
              const next = [...people];
              const row = next[i];
              if (!row) return;
              next[i] = { ...row, email: v || null };
              onChange(next);
            }}
          />
          <FieldInput
            label="Phone"
            value={p.phone ?? ''}
            onChange={(v) => {
              const next = [...people];
              const row = next[i];
              if (!row) return;
              next[i] = { ...row, phone: v || null };
              onChange(next);
            }}
          />
          <button
            type="button"
            className="self-end rounded-control border border-brand-border px-3 py-2 text-sm text-brand-muted hover:bg-brand-bg"
            onClick={() => onChange(people.filter((_, j) => j !== i))}
          >
            Remove
          </button>
        </div>
      ))}
      {(maxRows === undefined || people.length < maxRows) && (
        <button
          type="button"
          className="rounded-control border border-dashed border-brand-border px-3 py-2 text-sm text-brand-muted hover:bg-brand-bg"
          onClick={() => onChange([...people, blankPerson()])}
        >
          + {addLabel}
        </button>
      )}
    </div>
  );
}

function Field({
  label,
  value,
  readOnly,
}: {
  label: string;
  value: string;
  readOnly?: boolean;
}): JSX.Element {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wide text-brand-muted">{label}</span>
      <input
        readOnly={readOnly}
        value={value}
        className={`mt-1 w-full rounded border border-brand-border px-2 py-1.5 text-sm focus:border-brand-accent focus:outline-hidden ${
          readOnly ? 'bg-brand-bg/40 text-brand-muted' : 'bg-white'
        }`}
      />
    </label>
  );
}

function FieldInput({
  label,
  value,
  onChange,
  type = 'text',
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
}): JSX.Element {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wide text-brand-muted">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-control border border-brand-border bg-white px-2 py-1.5 text-sm focus:border-brand-accent focus:outline-hidden"
      />
    </label>
  );
}

function NameField({
  label,
  first_name,
  last_name,
  onChange,
  rowKey,
}: {
  label: string;
  first_name: string | null;
  last_name: string | null;
  onChange: (first: string | null, last: string | null) => void;
  rowKey: string;
}): JSX.Element {
  const initial = [first_name ?? '', last_name === null ? '' : ` ${last_name}`]
    .join('')
    .trimStart();
  const [draft, setDraft] = useState(initial);
  const [knownKey, setKnownKey] = useState(rowKey);

  if (rowKey !== knownKey) {
    setKnownKey(rowKey);
    setDraft(initial);
  }

  const handle = (v: string): void => {
    setDraft(v);
    const lastSpace = v.lastIndexOf(' ');
    if (lastSpace === -1) {
      onChange(v === '' ? null : v, null);
    } else {
      const first = v.slice(0, lastSpace);
      const last = v.slice(lastSpace + 1);
      onChange(first === '' ? null : first, last === '' ? '' : last);
    }
  };

  return <FieldInput label={label} value={draft} onChange={handle} />;
}

function blankPerson(): PersonnelPerson {
  return { id: '', first_name: null, last_name: null, email: null, phone: null };
}

export type ChurchType = 'parish' | 'branch';
export function classifyChurchType(churchType: string | null): ChurchType {
  if (!churchType) return 'parish';
  return /branch/i.test(churchType) ? 'branch' : 'parish';
}
